import React, { useEffect, useRef, useCallback } from 'react';

export default function InteractiveParticles() {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const shootingStarsRef = useRef([]);
  const planetsRef = useRef([]);
  const mouseRef = useRef({ x: null, y: null, radius: 150 });
  const animationRef = useRef(null);
  const lastTapRef = useRef({ x: 0, y: 0, time: 0 });

  // Dark mode colors only
  const colors = {
    background: 'linear-gradient(135deg, #0a0a1a 0%, #1a1a3e 30%, #0f0f2d 70%, #0a0a1a 100%)',
    particleHueRange: [250, 310], // Purple to blue
    connectionColor: 'rgba(139, 92, 246,',
    planetColors: [
      { color1: '#8b5cf6', color2: '#4c1d95', glowColor: 'rgba(139, 92, 246, 0.3)' },
      { color1: '#f472b6', color2: '#831843', glowColor: 'rgba(244, 114, 182, 0.3)' },
      { color1: '#60a5fa', color2: '#1e3a8a', glowColor: 'rgba(96, 165, 250, 0.3)' },
      { color1: '#a78bfa', color2: '#5b21b6', glowColor: 'rgba(167, 139, 250, 0.3)' },
      { color1: '#c084fc', color2: '#6b21a8', glowColor: 'rgba(192, 132, 252, 0.3)' },
    ],
    shootingStarColor: 'rgba(255, 255, 255,',
    shootingStarTrail: 'rgba(167, 139, 250,',
    mouseGlow: 'rgba(139, 92, 246,',
  };

  // Create a shooting star
  const createShootingStar = useCallback((canvas) => {
    const startX = Math.random() * canvas.width;
    const startY = Math.random() * (canvas.height * 0.5); // Top half of screen
    const angle = Math.PI / 4 + (Math.random() - 0.5) * 0.5; // Diagonal angle
    const speed = 8 + Math.random() * 12;
    
    return {
      x: startX,
      y: startY,
      length: 80 + Math.random() * 120,
      speed: speed,
      angle: angle,
      opacity: 1,
      thickness: 1 + Math.random() * 2,
      trail: [],
      maxTrailLength: 20,
      life: 1,
    };
  }, []);

  // Create a planet
  const createPlanet = useCallback((canvas, index) => {
    const type = colors.planetColors[index % colors.planetColors.length];
    const hasRing = index === 1 || index === 3; // Some planets have rings
    
    return {
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      baseX: Math.random() * canvas.width,
      baseY: Math.random() * canvas.height,
      size: 30 + Math.random() * 20,
      color1: type.color1,
      color2: type.color2,
      glowColor: type.glowColor,
      hasRing: hasRing,
      ringAngle: Math.random() * Math.PI,
      orbitSpeed: 0.0001 + Math.random() * 0.0002,
      orbitRadius: 50 + Math.random() * 100,
      orbitOffset: Math.random() * Math.PI * 2,
      rotationSpeed: 0.001 + Math.random() * 0.002,
      rotation: 0,
      opacity: 0.6 + Math.random() * 0.3,
    };
  }, [colors.planetColors]);

  const createParticle = useCallback((canvas, isNew = false, x = null, y = null) => {
    const baseX = x !== null ? x : Math.random() * canvas.width;
    const baseY = y !== null ? y : Math.random() * canvas.height;
    const hueMin = colors.particleHueRange[0];
    const hueMax = colors.particleHueRange[1];
    
    return {
      x: baseX,
      y: baseY,
      baseX: baseX,
      baseY: baseY,
      size: Math.random() * 3 + 1,
      speedX: (Math.random() - 0.5) * 0.5,
      speedY: (Math.random() - 0.5) * 0.5,
      opacity: Math.random() * 0.5 + 0.2,
      hue: Math.random() * (hueMax - hueMin) + hueMin,
      pulseSpeed: Math.random() * 0.02 + 0.01,
      pulseOffset: Math.random() * Math.PI * 2,
      isNew: isNew,
      life: isNew ? 1 : null,
      velocityX: isNew ? (Math.random() - 0.5) * 8 : 0,
      velocityY: isNew ? (Math.random() - 0.5) * 8 : 0,
    };
  }, [colors.particleHueRange]);

  const init = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particleCount = Math.min(Math.floor((canvas.width * canvas.height) / 8000), 150);
    particlesRef.current = [];

    for (let i = 0; i < particleCount; i++) {
      particlesRef.current.push(createParticle(canvas));
    }

    // Initialize planets (3-5 planets)
    const planetCount = 3 + Math.floor(Math.random() * 3);
    planetsRef.current = [];
    for (let i = 0; i < planetCount; i++) {
      planetsRef.current.push(createPlanet(canvas, i));
    }

    // Initialize shooting stars array
    shootingStarsRef.current = [];
  }, [createParticle, createPlanet]);

  const drawConnections = useCallback((ctx, particles) => {
    for (let i = 0; i < particles.length; i++) {
      for (let j = i + 1; j < particles.length; j++) {
        const dx = particles[i].x - particles[j].x;
        const dy = particles[i].y - particles[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 120) {
          const opacity = (1 - distance / 120) * 0.15;
          ctx.beginPath();
          ctx.strokeStyle = `${colors.connectionColor}${opacity})`;
          ctx.lineWidth = 0.5;
          ctx.moveTo(particles[i].x, particles[j].y);
          ctx.lineTo(particles[j].x, particles[j].y);
          ctx.stroke();
        }
      }
    }
  }, [colors.connectionColor]);

  // Draw a planet with optional ring
  const drawPlanet = useCallback((ctx, planet, time) => {
    // Update planet position (gentle orbit)
    planet.x = planet.baseX + Math.sin(time * planet.orbitSpeed + planet.orbitOffset) * planet.orbitRadius * 0.3;
    planet.y = planet.baseY + Math.cos(time * planet.orbitSpeed + planet.orbitOffset) * planet.orbitRadius * 0.2;
    planet.rotation += planet.rotationSpeed;

    ctx.save();
    ctx.globalAlpha = planet.opacity;

    // Outer glow
    const glowGradient = ctx.createRadialGradient(
      planet.x, planet.y, planet.size * 0.5,
      planet.x, planet.y, planet.size * 2
    );
    glowGradient.addColorStop(0, planet.glowColor);
    glowGradient.addColorStop(1, 'transparent');
    ctx.beginPath();
    ctx.fillStyle = glowGradient;
    ctx.arc(planet.x, planet.y, planet.size * 2, 0, Math.PI * 2);
    ctx.fill();

    // Planet body gradient
    const gradient = ctx.createRadialGradient(
      planet.x - planet.size * 0.3, planet.y - planet.size * 0.3, 0,
      planet.x, planet.y, planet.size
    );
    gradient.addColorStop(0, planet.color1);
    gradient.addColorStop(0.7, planet.color2);
    gradient.addColorStop(1, '#000');

    ctx.beginPath();
    ctx.fillStyle = gradient;
    ctx.arc(planet.x, planet.y, planet.size, 0, Math.PI * 2);
    ctx.fill();

    // Planet surface details (subtle bands)
    ctx.globalAlpha = planet.opacity * 0.3;
    for (let i = 0; i < 3; i++) {
      const bandY = planet.y - planet.size * 0.5 + (planet.size * i * 0.4);
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.lineWidth = 2;
      ctx.ellipse(planet.x, bandY, planet.size * 0.8, planet.size * 0.1, 0, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Ring (if planet has one)
    if (planet.hasRing) {
      ctx.globalAlpha = planet.opacity * 0.6;
      ctx.beginPath();
      ctx.strokeStyle = `rgba(255, 255, 255, 0.4)`;
      ctx.lineWidth = 3;
      ctx.ellipse(planet.x, planet.y, planet.size * 1.8, planet.size * 0.4, planet.ringAngle, 0, Math.PI * 2);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.strokeStyle = `rgba(167, 139, 250, 0.3)`;
      ctx.lineWidth = 6;
      ctx.ellipse(planet.x, planet.y, planet.size * 2, planet.size * 0.5, planet.ringAngle, 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.restore();
  }, []);

  // Draw a shooting star
  const drawShootingStar = useCallback((ctx, star) => {
    // Update position
    star.x += Math.cos(star.angle) * star.speed;
    star.y += Math.sin(star.angle) * star.speed;
    
    // Add to trail
    star.trail.unshift({ x: star.x, y: star.y });
    if (star.trail.length > star.maxTrailLength) {
      star.trail.pop();
    }
    
    // Fade out
    star.life -= 0.015;
    star.opacity = star.life;

    // Draw trail
    if (star.trail.length > 1) {
      ctx.beginPath();
      ctx.moveTo(star.trail[0].x, star.trail[0].y);
      
      for (let i = 1; i < star.trail.length; i++) {
        ctx.lineTo(star.trail[i].x, star.trail[i].y);
      }
      
      const gradient = ctx.createLinearGradient(
        star.trail[0].x, star.trail[0].y,
        star.trail[star.trail.length - 1].x, star.trail[star.trail.length - 1].y
      );
      gradient.addColorStop(0, `${colors.shootingStarColor}${star.opacity})`);
      gradient.addColorStop(0.3, `${colors.shootingStarTrail}${star.opacity * 0.8})`);
      gradient.addColorStop(1, 'transparent');
      
      ctx.strokeStyle = gradient;
      ctx.lineWidth = star.thickness;
      ctx.lineCap = 'round';
      ctx.stroke();

      // Bright head
      ctx.beginPath();
      ctx.fillStyle = `${colors.shootingStarColor}${star.opacity})`;
      ctx.arc(star.x, star.y, star.thickness * 1.5, 0, Math.PI * 2);
      ctx.fill();

      // Glow around head
      const headGlow = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, 10);
      headGlow.addColorStop(0, `${colors.shootingStarColor}${star.opacity * 0.8})`);
      headGlow.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.fillStyle = headGlow;
      ctx.arc(star.x, star.y, 10, 0, Math.PI * 2);
      ctx.fill();
    }
  }, [colors.shootingStarColor, colors.shootingStarTrail]);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const time = Date.now() * 0.001;
    const mouse = mouseRef.current;

    // Filter out dead particles and update
    particlesRef.current = particlesRef.current.filter(particle => {
      if (particle.isNew && particle.life <= 0) return false;
      return true;
    });

    particlesRef.current.forEach((particle) => {
      // Update new particles (from taps)
      if (particle.isNew) {
        particle.x += particle.velocityX;
        particle.y += particle.velocityY;
        particle.velocityX *= 0.98;
        particle.velocityY *= 0.98;
        particle.life -= 0.015;
        particle.opacity = particle.life * 0.8;
      } else {
        // Normal particle movement
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        // Gentle floating motion
        particle.x += Math.sin(time + particle.pulseOffset) * 0.3;
        particle.y += Math.cos(time + particle.pulseOffset) * 0.3;

        // Mouse interaction
        if (mouse.x !== null && mouse.y !== null) {
          const dx = mouse.x - particle.x;
          const dy = mouse.y - particle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < mouse.radius) {
            const force = (mouse.radius - distance) / mouse.radius;
            const angle = Math.atan2(dy, dx);
            
            // Push particles away from mouse
            particle.x -= Math.cos(angle) * force * 3;
            particle.y -= Math.sin(angle) * force * 3;
          }
        }

        // Slowly return to base position
        particle.x += (particle.baseX - particle.x) * 0.01;
        particle.y += (particle.baseY - particle.y) * 0.01;

        // Wrap around screen
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;
      }

      // Pulse effect
      const pulse = Math.sin(time * particle.pulseSpeed * 10 + particle.pulseOffset) * 0.5 + 0.5;
      const currentSize = particle.size * (1 + pulse * 0.3);
      const currentOpacity = particle.opacity * (0.7 + pulse * 0.3);

      // Draw particle glow
      const gradient = ctx.createRadialGradient(
        particle.x, particle.y, 0,
        particle.x, particle.y, currentSize * 4
      );
      gradient.addColorStop(0, `hsla(${particle.hue}, 80%, 70%, ${currentOpacity})`);
      gradient.addColorStop(0.5, `hsla(${particle.hue}, 70%, 60%, ${currentOpacity * 0.3})`);
      gradient.addColorStop(1, 'transparent');

      ctx.beginPath();
      ctx.fillStyle = gradient;
      ctx.arc(particle.x, particle.y, currentSize * 4, 0, Math.PI * 2);
      ctx.fill();

      // Draw particle core
      ctx.beginPath();
      ctx.fillStyle = `hsla(${particle.hue}, 80%, 80%, ${currentOpacity})`;
      ctx.arc(particle.x, particle.y, currentSize, 0, Math.PI * 2);
      ctx.fill();
    });

    // Draw connections between nearby particles
    drawConnections(ctx, particlesRef.current.filter(p => !p.isNew));

    // Draw planets (behind particles, so draw first)
    planetsRef.current.forEach(planet => {
      drawPlanet(ctx, planet, time);
    });

    // Randomly spawn shooting stars
    if (Math.random() < 0.008) { // ~0.8% chance per frame = roughly every 2-3 seconds
      shootingStarsRef.current.push(createShootingStar(canvas));
    }

    // Update and draw shooting stars
    shootingStarsRef.current = shootingStarsRef.current.filter(star => star.life > 0);
    shootingStarsRef.current.forEach(star => {
      drawShootingStar(ctx, star);
    });

    // Draw mouse glow effect
    if (mouse.x !== null && mouse.y !== null) {
      const mouseGradient = ctx.createRadialGradient(
        mouse.x, mouse.y, 0,
        mouse.x, mouse.y, mouse.radius
      );
      mouseGradient.addColorStop(0, `${colors.mouseGlow}0.1)`);
      mouseGradient.addColorStop(0.5, `${colors.mouseGlow}0.05)`);
      mouseGradient.addColorStop(1, 'transparent');

      ctx.beginPath();
      ctx.fillStyle = mouseGradient;
      ctx.arc(mouse.x, mouse.y, mouse.radius, 0, Math.PI * 2);
      ctx.fill();
    }

    animationRef.current = requestAnimationFrame(animate);
  }, [drawConnections, drawPlanet, drawShootingStar, createShootingStar, colors.mouseGlow]);

  const handleMouseMove = useCallback((e) => {
    mouseRef.current.x = e.clientX;
    mouseRef.current.y = e.clientY;
  }, []);

  const handleMouseLeave = useCallback(() => {
    mouseRef.current.x = null;
    mouseRef.current.y = null;
  }, []);

  const handleTap = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let x, y;
    if (e.type === 'touchstart' && e.touches.length > 0) {
      x = e.touches[0].clientX;
      y = e.touches[0].clientY;
    } else if (e.type === 'click') {
      x = e.clientX;
      y = e.clientY;
    } else {
      return;
    }

    // Create burst of particles at tap location
    const burstCount = 15;
    for (let i = 0; i < burstCount; i++) {
      const particle = createParticle(canvas, true, x, y);
      particlesRef.current.push(particle);
    }

    lastTapRef.current = { x, y, time: Date.now() };
  }, [createParticle]);

  useEffect(() => {
    init();
    animate();

    const handleResize = () => {
      init();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [init, animate, handleMouseMove, handleMouseLeave]);

  return (
    <canvas
      ref={canvasRef}
      onClick={handleTap}
      onTouchStart={handleTap}
      className="fixed inset-0 pointer-events-none z-0"
      style={{
        background: colors.background,
        touchAction: 'none',
        WebkitOverflowScrolling: 'none',
        overscrollBehavior: 'none',
      }}
    />
  );
}
